<template>
    <div class="feedback-container">
        <!-- 页面标题 -->
        <div class="page-header">
            <h1 class="title">待处理反馈</h1>
            <p class="subtitle">当前有 {{ pendingCount }} 条未解决的反馈</p>
        </div>

        <!-- 筛选和搜索区域 -->
        <div class="filter-section">
            <el-row :gutter="20">
                <el-col :span="14">
                    <el-input v-model="searchQuery" placeholder="搜索反馈内容/UID/联系方式..." prefix-icon="Search" clearable
                        @clear="fetchFeedbacks" @keyup.enter="applyFilters" />
                </el-col>
                <el-col :span="7">
                    <el-select v-model="filterType" placeholder="筛选问题类型" @change="applyFilters">
                        <el-option label="全部类型" value="all" />
                        <el-option v-for="type in feedbackTypes" :key="type" :label="type" :value="type" />
                    </el-select>
                </el-col>
                <el-col :span="3">
                    <el-button type="primary" @click="fetchFeedbacks" class="refresh-button">
                        <el-icon size="18" >
                            <Refresh />
                        </el-icon>刷新
                    </el-button>
                </el-col>
            </el-row>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-state">
            <el-skeleton :rows="4" animated />
        </div>

        <!-- 反馈列表 -->
        <div v-else class="feedback-list">
            <el-card v-for="(item, index) in filteredData" :key="item.id || index" class="feedback-card" shadow="hover">
                <template #header>
                    <div class="card-header">
                        <div class="user-info">
                            <div class="user-details">
                                <div class="username">B站UID：{{ item.userId }}</div>
                                <div class="time">{{ formatTime(item.time) }}</div>
                            </div>
                        </div>
                        <div class="problem-badge" :class="getBadgeClass(item.problemType)">
                            {{ item.problemType }}
                        </div>
                    </div>
                </template>
                <div class="card-content">
                    <p class="problem-desc">反馈内容：{{ item.problemDesc }}</p>
                    <div class="contact-info">
                        <el-tag type="info">QQ联系方式: {{ item.contactInfo }}</el-tag>
                    </div>
                </div>
                <div class="card-footer">
                    <el-button type="primary" size="small" :loading="item.processing" @click="markAsSolved(item)">
                        <span v-if="!item.processing">标记为已解决</span>
                        <span v-else>处理中...</span>
                    </el-button>
                </div>
            </el-card>
        </div>

        <!-- 空状态 -->
        <div v-if="!loading && filteredData.length === 0" class="empty-state">
            <div class="empty-icon">
                <el-icon>
                    <DocumentEmpty />
                </el-icon>
            </div>
            <p class="empty-text">暂无未解决的反馈</p>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { DocumentEmpty, Search, Refresh } from '@element-plus/icons-vue';
import axios from 'axios';
import dayjs from 'dayjs';
import { ElMessage } from 'element-plus';

// 反馈类型常量
const feedbackTypes = [
    '加群审核',
    '禁言申诉',
    '私信补发',
    '音声礼物',
    '物流相关',
    '其他'
];

// 状态管理
const loading = ref(false);
const feedbackData = ref([]);
const searchQuery = ref('');
const filterType = ref('all');

// 获取反馈列表
const fetchFeedbacks = async () => {
    loading.value = true;

    try {
        const response = await axios.get('/adminapi/Feedback/list');

        if (response.data && Array.isArray(response.data.results)) {
            feedbackData.value = response.data.results.map(item => ({
                ...item,
                processing: false
            }));
        } else {
            ElMessage.error('获取反馈列表失败');
        }
    } catch (error) {
        console.error('获取反馈列表错误:', error);
        ElMessage.error('网络错误，请稍后重试');
    } finally {
        loading.value = false;
    }
};

// 应用筛选和搜索
const applyFilters = () => {
    // 不需要重新请求，使用计算属性进行客户端筛选
};

// 计算属性：只显示未解决的反馈，并应用筛选
const filteredData = computed(() => {
    let data = feedbackData.value
        .filter(item => item.Solve === '未解决');

    // 应用类型筛选
    if (filterType.value !== 'all') {
        data = data.filter(item => item.problemType === filterType.value);
    }

    // 应用搜索筛选
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        data = data.filter(item =>
            (item.userId && item.userId.toLowerCase().includes(query)) ||
            (item.contactInfo && item.contactInfo.toLowerCase().includes(query)) ||
            (item.problemDesc && item.problemDesc.toLowerCase().includes(query))
        );
    }

    // 按时间倒序排序
    return data.sort((a, b) => dayjs(b.time).diff(dayjs(a.time)));
});

// 计算未解决反馈数量
const pendingCount = computed(() => filteredData.value.length);

// 格式化时间
const formatTime = (time) => {
    if (!time) return '';
    return dayjs(time).format('YYYY-MM-DD HH:mm');
};

// 获取徽章类
const getBadgeClass = (type) => {
    const badgeMap = {
        '加群审核': 'join',
        '禁言申诉': 'mute',
        '私信补发': 'message',
        '音声礼物': 'voice',
        '物流相关': 'logistics',
        '其他': 'other'
    };
    return `badge-${badgeMap[type] || 'default'}`;
};

// 标记为已解决
const markAsSolved = async (item) => {
    item.processing = true;

    try {
        // 写操作走 DELETE（后端路由已从 GET 改过来）
        const response = await axios.delete(`/adminapi/Feedback/${item.id}`);

        if (response.data.code === 200) {
            // 本地更新状态
            const index = feedbackData.value.findIndex(f => f.id === item.id);
            if (index !== -1) {
                feedbackData.value[index].Solve = '已解决';
            }
            ElMessage.success('已标记为已解决');
            fetchFeedbacks();
        } else {
            ElMessage.error(response.data.message || '操作失败');
        }
    } catch (error) {
        console.error('标记解决错误:', error);
        ElMessage.error('网络错误，请稍后重试');
    } finally {
        item.processing = false;
    }
};

onMounted(() => {
    fetchFeedbacks();
});
</script>

<style scoped>
/* 基础样式 */
.feedback-container {
    min-height: 100vh;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #f8f9fa;
}

/* 页面标题 */
.page-header {
    width: 100%;
    max-width: 1200px;
    padding: 10px 0;
    margin-bottom: 40px;
    text-align: center;
}

.title {
    font-size: 32px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
}

.subtitle {
    font-size: 18px;
    color: #666;
}

/* 筛选区域 */
.filter-section {
    width: 100%;
    max-width: 1200px;
    padding: 20px;
    margin-bottom: 30px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

/* 加载状态 */
.loading-state {
    width: 100%;
    max-width: 1200px;
}

/* 反馈列表 */
.feedback-list {
    width: 100%;
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-bottom: 50px;
}

.feedback-card {
    padding: 20px;
    transition: all 0.3s ease;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 15px;
}

.username {
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.time {
    font-size: 14px;
    color: #999;
}

.problem-badge {
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
}

.card-content {
    margin-bottom: 20px;
}

.problem-desc {
    font-size: 16px;
    color: #333;
    line-height: 1.6;
    margin-bottom: 15px;
}

.contact-info {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.card-footer {
    display: flex;
    justify-content: flex-end;
}

/* 空状态 */
.empty-state {
    width: 100%;
    max-width: 600px;
    padding: 60px 40px;
    text-align: center;
    margin-top: 50px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.empty-icon {
    font-size: 64px;
    color: #ccc;
    margin-bottom: 20px;
}

.empty-text {
    font-size: 18px;
    color: #666;
}

/* 响应式调整 */
@media (max-width: 768px) {
    .feedback-container {
        padding: 10px;
    }

    .page-header {
        padding: 20px 0;
    }

    .title {
        font-size: 24px;
    }

    .subtitle {
        font-size: 16px;
    }

    .filter-section {
        padding: 15px;
    }

    .feedback-card {
        padding: 15px;
    }

    .username {
        font-size: 16px;
    }

    .time {
        font-size: 12px;
    }

    .problem-desc {
        font-size: 14px;
    }

    .problem-badge {
        padding: 3px 8px;
        font-size: 12px;
    }
}

/* 徽章颜色 */
.badge-join {
    background: rgba(33, 150, 243, 0.1);
    color: #2196f3;
}

.badge-mute {
    background: rgba(233, 30, 99, 0.1);
    color: #e91e63;
}

.badge-message {
    background: rgba(156, 39, 176, 0.1);
    color: #9c27b0;
}

.badge-voice {
    background: rgba(76, 175, 80, 0.1);
    color: #4caf50;
}

.badge-logistics {
    background: rgba(255, 152, 0, 0.1);
    color: #ff9800;
}

.badge-other {
    background: rgba(121, 85, 72, 0.1);
    color: #795548;
}

.badge-default {
    background: rgba(158, 158, 158, 0.1);
    color: #9e9e9e;
}

.refresh-button {
    width: 100%;
    transition: all 0.3s ease;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
    }
}
</style>